import { useMemo, useState } from "react";
import contacten from "../../data/pm26-contacten.json";

// Contactenlijst showdag — tik = bellen. Kern-contacten bovenaan,
// leveranciers eronder, zoekveld filtert op naam/rol.
// Bron PM26: contactsecties veiligheidsplan V4 §2.1 + medisch plan v3.1 §2 —
// alle nummers individueel geverifieerd tegen de brontekst (17-07).
// + WhatsApp-veiligheidsgroep (EMS/reddingsbrigade) + CP-briefing.

interface Contact {
  naam: string;
  rol: string;
  tel: string;
  telWeergave: string;
  mail: string;
  kern: boolean;
}

const ALLE = contacten as Contact[];

// Leesbaar NL-formaat: +31612345678 -> 06 12 34 56 78; vaste nummers per 2 gegroepeerd
function formatNummer(tel: string): string {
  const d = tel.replace(/\D/g, "");
  const nl = d.startsWith("31") ? "0" + d.slice(2) : d;
  if (nl.startsWith("06") && nl.length === 10) {
    return `06 ${nl.slice(2, 4)} ${nl.slice(4, 6)} ${nl.slice(6, 8)} ${nl.slice(8, 10)}`;
  }
  return nl.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

function ContactRow({ c }: { c: Contact }) {
  return (
    <li className="card flex items-center gap-3 p-2.5">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-200 truncate">{c.naam}</div>
        <div className="text-2xs text-zinc-500 truncate">
          {c.rol}
          {c.mail ? (
            <>
              {" | "}
              <a href={`mailto:${c.mail}`} className="underline underline-offset-2">
                mail
              </a>
            </>
          ) : null}
        </div>
      </div>
      <a
        href={`tel:${c.tel}`}
        aria-label={`Bel ${c.naam}`}
        className="shrink-0 font-mono tabular text-base text-zinc-100 tracking-wide py-2"
      >
        {formatNummer(c.tel)}
      </a>
    </li>
  );
}

export function Contacten() {
  const [q, setQ] = useState("");

  const { kern, rest } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (c: Contact) =>
      !needle ||
      c.naam.toLowerCase().includes(needle) ||
      c.rol.toLowerCase().includes(needle);
    return {
      kern: ALLE.filter((c) => c.kern && match(c)),
      rest: ALLE.filter((c) => !c.kern && match(c)),
    };
  }, [q]);

  return (
    <div className="space-y-2">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Zoek naam of rol"
        aria-label="Zoek contact"
        className="w-full rounded-lg border border-line bg-ink-900 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-accent/60 focus:outline-none"
      />
      {kern.length > 0 && (
        <ul className="space-y-1.5">
          {kern.map((c) => (
            <ContactRow key={`${c.naam}-${c.tel}`} c={c} />
          ))}
        </ul>
      )}
      {rest.length > 0 && (
        <>
          <div className="text-2xs text-zinc-600 uppercase tracking-widest px-1 pt-1.5">
            Leveranciers
          </div>
          <ul className="space-y-1.5">
            {rest.map((c) => (
              <ContactRow key={`${c.naam}-${c.tel}`} c={c} />
            ))}
          </ul>
        </>
      )}
      {kern.length === 0 && rest.length === 0 && (
        <p className="text-sm text-zinc-500 px-1 py-2">
          Geen contact gevonden voor &ldquo;{q}&rdquo;.
        </p>
      )}
    </div>
  );
}
